import { useEffect, useRef, useState } from 'react';
import ModalOverlay from '@/components/base/ModalOverlay';
import GlowButton from '@/components/base/GlowButton';

interface ContactCard {
  icon: string;
  iconBg: string;
  iconColor: string;
  tag: string;
  tagBg: string;
  title: string;
  desc: string;
  points: string[];
  telegram: string;
  cta: string;
  accent: string;
  glowColor: string;
  checkColor: string;
  taglineColor: string;
  dividerColor: string;
}

interface ContactModalProps {
  open: boolean;
  card: ContactCard | null;
  onClose: () => void;
}

export default function ContactModal({ open, card, onClose }: ContactModalProps) {
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState('');
  const textRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setSent(false);
      setMsg('');
    }
  }, [open, card]);

  if (!card) return null;

  return (
    <ModalOverlay open={open} onClose={onClose} zIndex={80}>
      <div className="w-[480px] max-w-[calc(100vw-2rem)] bg-white/95 backdrop-blur-2xl border border-slate-200/60 rounded-2xl overflow-hidden">
        {/* Top accent bar */}
        <div className="h-[3px] w-full" style={{ background: `linear-gradient(90deg, transparent 0%, ${card.accent} 35%, ${card.accent} 65%, transparent 100%)` }} />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100/80">
          <div className="flex items-center gap-3.5">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${card.iconBg} border border-white/80 shrink-0`}>
              <i className={`${card.icon} text-xl ${card.iconColor}`}></i>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">{card.title}</h3>
              <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${card.tagBg}`}>{card.tag}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100/60 rounded-lg transition-all cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {sent ? (
            <div className="flex flex-col items-center text-center py-8">
              <div
                className="w-16 h-16 flex items-center justify-center rounded-full mb-5 border-2"
                style={{ background: card.glowColor, borderColor: `${card.accent}44` }}
              >
                <i className="ri-check-double-line text-2xl" style={{ color: card.accent }}></i>
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-2">Message Sent!</h4>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed max-w-xs">
                We'll reach out via Telegram shortly. Thanks for getting in touch!
              </p>
              <GlowButton onClick={onClose} className="px-8 py-2.5 text-sm font-semibold rounded-xl">
                Done
              </GlowButton>
            </div>
          ) : (
            <>
              {/* Description */}
              <p className="text-sm text-slate-500 leading-relaxed mb-5">{card.desc}</p>

              {/* Points */}
              <ul className="flex flex-col gap-3 mb-6">
                {card.points.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-slate-600">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`ri-checkbox-circle-line text-lg ${card.checkColor}`}></i>
                    </span>
                    {p}
                  </li>
                ))}
              </ul>

              {/* Divider */}
              <div
                className={`w-full h-px mb-5 border-t ${card.dividerColor}`}
                style={{ boxShadow: '0 1px 0 rgba(255,255,255,0.7)' }}
              />

              {/* Quick message */}
              <p className="text-xs font-semibold text-slate-500 mb-2.5 uppercase tracking-wider">
                Send a quick note <span className="normal-case font-normal text-slate-400">(optional)</span>
              </p>
              <textarea
                ref={textRef}
                value={msg}
                onChange={(e) => setMsg(e.target.value.slice(0, 500))}
                placeholder="Briefly describe your inquiry..."
                rows={3}
                className="w-full px-4 py-3 text-sm bg-white/60 backdrop-blur-sm border border-white/70 rounded-xl text-slate-700 placeholder-slate-300 focus:outline-none focus:border-slate-300/70 focus:bg-white/80 transition-colors resize-none"
              />
              <p className={`text-right text-[11px] mt-1.5 mb-5 ${msg.length > 460 ? 'text-red-400' : 'text-slate-300'}`}>
                {msg.length}/500
              </p>

              {/* CTA */}
              <GlowButton
                as="a"
                href={`https://t.me/${card.telegram}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={() => setSent(true)}
                className="w-full py-3.5 text-sm font-bold rounded-xl"
              >
                <i className="ri-telegram-fill text-base"></i>
                {card.cta}
              </GlowButton>
              <p className={`text-center text-[11px] mt-2.5 ${card.taglineColor}`}>
                @{card.telegram} · Usually replies within 24h
              </p>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
